import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import SearchIcon from "@mui/icons-material/Search";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CategoryIcon from "@mui/icons-material/Category";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  get_material_categories,
  get_material_sub_categories,
  get_material_sub_sub_categories,
  get_material_brands,
} from "../../api/ControllApi";

const DEBOUNCE_MS = 500; // delay for search input keystroke

const MaterialFilterCard = ({ filters, onFilterChange }) => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [loadingSubSubs, setLoadingSubSubs] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.material_name || "");

  // Ref to hold the debounce timer for the search input
  const debounceTimer = useRef(null);

  // ── Initial fetch ──────────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingCats(true);
    get_material_categories()
      .then((res) => {
        const data =
          res?.data?.data?.categories_list ||
          res?.data?.categories_list ||
          res?.data?.data ||
          res?.data?.categories ||
          res?.data || [];
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoadingCats(false));

    get_material_brands()
      .then((res) => {
        const data =
          res?.data?.data?.brand_list ||
          res?.data?.brand_list ||
          res?.data?.data ||
          res?.data?.brands ||
          res?.data || [];
        setBrands(Array.isArray(data) ? data : []);
      })
      .catch(() => setBrands([]));
  }, []);

  // ── Fetch sub categories when category changes ─────────────────────────────
  useEffect(() => {
    if (filters.category) {
      setLoadingSubs(true);
      get_material_sub_categories(filters.category)
        .then((res) => {
          const data =
            res?.data?.data?.categories_list ||
            res?.data?.categories_list ||
            res?.data?.data ||
            res?.data?.sub_categories ||
            res?.data || [];
          setSubCategories(Array.isArray(data) ? data : []);
        })
        .catch(() => setSubCategories([]))
        .finally(() => setLoadingSubs(false));
    } else {
      setSubCategories([]);
    }
  }, [filters.category]);

  // ── Fetch sub sub categories when sub category changes ────────────────────
  useEffect(() => {
    if (filters.sub_category) {
      setLoadingSubSubs(true);
      get_material_sub_sub_categories(filters.sub_category)
        .then((res) => {
          const data =
            res?.data?.data?.categories_list ||
            res?.data?.data?.sub_sub_categories_list ||
            res?.data?.categories_list ||
            res?.data?.data ||
            res?.data?.sub_sub_categories ||
            res?.data || [];
          setSubSubCategories(Array.isArray(data) ? data : []);
        })
        .catch(() => setSubSubCategories([]))
        .finally(() => setLoadingSubSubs(false));
    } else {
      setSubSubCategories([]);
    }
  }, [filters.sub_category]);

  // ── Auto search: debounced on search input keystroke ──────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);

    // Clear previous timer and start a new one
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      onFilterChange({ material_name: val });
    }, DEBOUNCE_MS);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // ── Auto search: immediate on dropdown changes ────────────────────────────
  const handleCategoryChange = (val) => {
    onFilterChange({
      category: val,
      sub_category: "",
      sub_sub_category: [],
    });
    // onFilterChange itself triggers re-fetch in the parent — no extra call needed
  };

  const handleSubCategoryChange = (val) => {
    onFilterChange({
      sub_category: val,
      sub_sub_category: [],
    });
  };

  const handleSubSubCategoryChange = (val) => {
    onFilterChange({ sub_sub_category: val ? [val] : [] });
  };

  const handleBrandChange = (_, newValue) => {
    onFilterChange({ brand_id: newValue.map((v) => v.id) });
  };

  const handleClearFilters = () => {
    // Clear debounce timer too
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSearchValue("");
    onFilterChange({
      category: "",
      sub_category: "",
      sub_sub_category: [],
      brand_id: [],
      material_name: "",
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        py: 5,
        px: 3,
        backgroundImage: `url("assets/images/plootbg.png")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        borderRadius: 6,
        boxShadow: 3,
        color: "black",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography fontSize={{ xs: 13, md: 17 }} sx={{ fontWeight: 500, mb: 1 }}>
          Filter
        </Typography>
      </Box>
      <Box sx={{ width: 60, height: 4, bgcolor: "#FEB821", borderRadius: 2, mb: 3 }} />

      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
        Search products
      </Typography>

      {/* ── Search Input (auto search on keystroke, debounced) ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          background: "#FFFFFF",
          border: "1px solid #E5E5E5",
          height: 40,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <TextField
          placeholder='Search Materials'
          variant="standard"
          value={searchValue}
          onChange={handleSearchChange}           // ← auto search on every keystroke
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              if (debounceTimer.current) clearTimeout(debounceTimer.current);
              onFilterChange({ material_name: searchValue }); // immediate on Enter
            }
          }}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#B5B5B5", fontSize: 20, ml: 1 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            "& .MuiInputBase-input": {
              padding: "10px 12px",
              fontSize: 14,
            },
          }}
        />
        {/* Search button still works for manual trigger */}
        <Button
          variant="contained"
          onClick={() => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            onFilterChange({ material_name: searchValue });
          }}
          sx={{
            height: "100%",
            borderRadius: 0,
            px: 3,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            background: "linear-gradient(180deg, #C7DAA1 0%, #A8C672 100%)",
            color: "#000",
            "&:hover": {
              background: "linear-gradient(180deg, #BBD690 0%, #9DBD65 100%)",
            },
          }}
        >
          Search
        </Button>
      </Box>

      {/* ── Category (auto triggers onFilterChange immediately) ── */}
      <FilterSelect
        label="Material Category"
        placeholder="Select a category"
        icon={<VolunteerActivismIcon sx={{ fontSize: 20, color: "#FEB821", mr: 1 }} />}
        value={filters.category}
        onChange={handleCategoryChange}
        options={categories}
        loading={loadingCats}
        displayKey="category_name"
        valueKey="id"
      />

      {/* ── Sub Category ── */}
      <FilterSelect
        label="Sub Category"
        placeholder="Select a sub category"
        icon={<CategoryIcon sx={{ fontSize: 20, color: "#FEB821", mr: 1 }} />}
        value={filters.sub_category}
        onChange={handleSubCategoryChange}
        options={subCategories}
        loading={loadingSubs}
        disabled={!filters.category}
        displayKey="name"
        valueKey="id"
      />

      {/* ── Sub Sub Category ── */}
      <FilterSelect
        label="Sub Sub Category"
        placeholder="Select a sub sub category"
        icon={<CategoryIcon sx={{ fontSize: 20, color: "#FEB821", mr: 1 }} />}
        value={filters.sub_sub_category[0] || ""}
        onChange={handleSubSubCategoryChange}
        options={subSubCategories}
        loading={loadingSubSubs}
        disabled={!filters.sub_category}
        displayKey="name"
        valueKey="id"
      />

      {/* ── Brand Multi-Select (auto triggers on every selection change) ── */}
      <Box mb={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <BrandingWatermarkIcon sx={{ fontSize: 20, color: "#FEB821", mr: 1 }} />
          <Typography variant="subtitle2">Product Brand</Typography>
        </Box>
        <Autocomplete
          multiple
          options={brands || []}
          getOptionLabel={(option) => option.name || ""}
          value={brands.filter((b) =>
            Array.isArray(filters.brand_id)
              ? filters.brand_id.includes(b.id)
              : filters.brand_id === b.id
          )}
          onChange={handleBrandChange}           // ← auto search on every brand select/deselect
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography fontSize={14}>{option.name}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                !filters.brand_id || filters.brand_id.length === 0
                  ? "Search & Select Brands"
                  : ""
              }
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  fontSize: 14,
                },
              }}
            />
          )}
        />
      </Box>

      <Box height={40} />

      <Button
        fullWidth
        variant="outlined"
        onClick={handleClearFilters}
        sx={{
          borderColor: "#649508",
          color: "#000",
          bgcolor:"rgba(168, 198, 114, 0.1)",
          "&:hover": { borderColor: "#8BA65B", bgcolor: "rgba(171, 216, 86, 0.1)" },
        }}
      >
        Clear Filters
      </Button>

      <Box height={120} />
    </Box>
  );
};

// ─── FilterSelect with placeholder ──────────────────────────────────────────
const FilterSelect = ({
  label,
  placeholder,
  icon,
  value,
  onChange,
  options,
  loading,
  disabled,
  displayKey,
  valueKey,
}) => (
  <Box mb={3}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      {icon}
      <Typography variant="subtitle2">{label}</Typography>
      {loading && <CircularProgress size={16} sx={{ ml: 1, color: "#FEB821" }} />}
    </Box>
    <TextField
      select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      size="small"
      variant="outlined"
      disabled={disabled}
      SelectProps={{
        displayEmpty: true,
        renderValue: (selected) => {
          if (!selected) {
            return (
              <Typography sx={{ color: "#aaa", fontSize: 14 }}>
                {placeholder || `Select ${label}`}
              </Typography>
            );
          }
          const found = Array.isArray(options)
            ? options.find((o) => o[valueKey] === selected)
            : null;
          return found ? found[displayKey] : selected;
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: "#fff",
          "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.5)" },
        },
      }}
    >
      <MenuItem value="">
        <em style={{ color: "#aaa" }}>All {label}s</em>
      </MenuItem>
      {Array.isArray(options) &&
        options.map((opt) => (
          <MenuItem key={opt[valueKey]} value={opt[valueKey]}>
            {opt[displayKey]}
          </MenuItem>
        ))}
    </TextField>
  </Box>
);

export default MaterialFilterCard;